import React, { useEffect, useState } from 'react';
import { useTheme, Box, IconButton, Typography, Switch } from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import ReactAce from 'react-ace';
import 'ace-builds/src-noconflict/mode-html';
import 'ace-builds/src-noconflict/theme-monokai';
import { HTMLHint } from 'htmlhint';
import { html as beautifyHtml } from 'js-beautify';
import ace from 'ace-builds';
import { useTranslation } from 'react-i18next';

const CodeEditor = ({
  data,
  setData,
  editorRef = null,
  height = 800,
  className = '',
  variables = [],
  currentKey,
  metaData,
  disableTemplateRendering,
  changeDisableTemplateRendering,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const [code, setCode] = useState(data);

  useEffect(() => {
    setCode(data);
  }, [currentKey]);

  const handleCodeChange = (newCode) => {
    setCode(newCode);
    const editor = editorRef.current.editor;
    const annotations = lintHtml(newCode);
    editor.getSession().setAnnotations(annotations);
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
      undo();
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
      redo();
    }
  };

  function undo() {
    editorRef.current.editor.undo();
  }

  function redo() {
    editorRef.current.editor.redo();
  }

  useEffect(() => {
    const editor = editorRef.current.editor;
    if (!editor) {
      return;
    }

    editor.container.addEventListener('keydown', handleKeyDown);

    return () => {
      editor.container.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const htmlHintConfig = {
    'doctype-first': false, // Disable "Doctype must be declared"
    'tag-pair': true, // Example: still check for unclosed tags
    'id-unique': true, // Example: still check for duplicate IDs
    // ...you can add more rules if needed
  };
  function lintHtml(code) {
    const results = HTMLHint.verify(code, htmlHintConfig);
    return results.map((err) => ({
      row: err.line - 1,
      column: err.col - 1,
      text: err.message,
      type: 'error',
    }));
  }
  // Register Cmd+S / Ctrl+S
  useEffect(() => {
    const editor = editorRef.current.editor;

    const handleFormatCommand = (event) => {
      // Prevent browser save
      if (event) event.preventDefault();

      const code = editor.getValue();
      const formatted = beautifyHtml(code, {
        indent_size: 2,
        wrap_line_length: 80,
      });
      setData(code);
      editor.setValue(formatted, 1);
    };

    editor.commands.addCommand({
      name: 'formatOnSave',
      bindKey: { win: 'Ctrl-S', mac: 'Command-S' },
      exec: () => handleFormatCommand(),
      readOnly: false,
    });

    // 🔥 Extra safety: globally stop browser save shortcut
    const preventSave = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        handleFormatCommand();
      }
    };

    window.addEventListener('keydown', preventSave);
    return () => window.removeEventListener('keydown', preventSave);
  }, [currentKey]);

  // variable highlighting
  const [highlightVariables, setHighlightVariables] = useState(true);
  useEffect(() => {
    if (!highlightVariables) {
      // cleanup if disabled
      const contentLayer =
        editorRef.current.editor.renderer.container.querySelector(
          '.ace_content'
        );
      contentLayer?.querySelectorAll('.var-chip').forEach((el) => el.remove());
      return;
    }

    const editor = editorRef.current.editor;
    const session = editor.getSession();
    const renderer = editor.renderer;

    editor.container.style.fontFamily = 'monospace';

    const pattern = new RegExp(
      variables
        .map((v) => v.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('|'),
      'g'
    );

    const contentLayer = renderer.container.querySelector('.ace_content');

    const renderVariableChips = () => {
      contentLayer.querySelectorAll('.var-chip').forEach((el) => el.remove());

      const config = renderer.layerConfig;
      const firstRow = renderer.getFirstVisibleRow();
      const lastRow = renderer.getLastVisibleRow();

      for (let row = firstRow; row <= lastRow; row++) {
        const line = session.getLine(row);
        if (!line) continue;

        let match;
        while ((match = pattern.exec(line)) !== null) {
          const variable = variables.find((v) => v.value === match[0]);
          if (!variable) continue;

          const startCol = match.index;
          const endCol = startCol + match[0].length;

          const start = session.documentToScreenPosition(row, startCol);
          const end = session.documentToScreenPosition(row, endCol);

          const top = renderer.$cursorLayer.getPixelPosition(
            { row: start.row, column: 0 },
            true
          ).top;
          const left = start.column * renderer.characterWidth;
          const width = (end.column - start.column) * renderer.characterWidth;
          const height = renderer.lineHeight - 2;

          const chip = document.createElement('div');
          chip.className = 'var-chip';
          chip.textContent = variable.name;
          chip.style.position = 'absolute';
          chip.style.left = `${left + 4}px`;
          chip.style.top = `${top - 1}px`;
          chip.style.width = `${width}px`;
          chip.style.height = `${height + 4}px`;
          chip.style.zIndex = 10;
          chip.style.padding = '2px 0px';
          chip.style.textAlign = 'center';
          contentLayer.appendChild(chip);
        }
      }
    };

    const rerender = () => renderVariableChips();

    editor.on('change', rerender);
    renderer.on('afterRender', rerender);
    session.on('changeScrollTop', rerender);
    session.on('changeScrollLeft', rerender);

    renderVariableChips();

    return () => {
      editor.off('change', rerender);
      renderer.off('afterRender', rerender);
      session.off('changeScrollTop', rerender);
      session.off('changeScrollLeft', rerender);

      contentLayer.querySelectorAll('.var-chip').forEach((el) => el.remove());
    };
  }, [highlightVariables]);

  return (
    <Box
      className="w-full h-full flex flex-col gap-1 relative"
      sx={{
        '.var-chip': {
          position: 'absolut',
          backgroundColor: colors.info,
          color: '#000',
          fontSize: '10px',
          padding: '0 4px',
          borderRadius: ' 3px',
          pointerEvents: ' none',
          fontFamily: ' monospace',
          whiteSpace: ' nowrap',
        },
      }}
    >
      <Box className="flex pl-2">
        {data === code ? (
          <Typography
            sx={{ bgcolor: colors.success + '30', color: colors.success }}
            fontWeight={500}
            className="px-3 py-1 rounded-full"
          >
            {t('Saved')}
          </Typography>
        ) : (
          <Box className="flex gap-2 items-center">
            <Typography
              sx={{ bgcolor: colors.warning + '30', color: colors.warning }}
              fontWeight={500}
              className="px-3 py-1 rounded-full"
            >
              {t('Unsaved Changes')}
            </Typography>
            <Typography>press STRG + S</Typography>
          </Box>
        )}
        <Box className="flex items-center">
          <Switch
            checked={highlightVariables}
            onChange={() => setHighlightVariables(!highlightVariables)}
          />
          <Typography>{t('Highlighting')}</Typography>
        </Box>
        <Box className="flex items-center">
          <Switch
            checked={!disableTemplateRendering}
            onChange={() =>
              changeDisableTemplateRendering(!disableTemplateRendering)
            }
          />
          <Typography>{t('Variables Rendering')}</Typography>
        </Box>
      </Box>
      <ReactAce
        mode="html"
        theme="monokai"
        onChange={handleCodeChange}
        value={code}
        highlightActiveLine={true}
        ref={editorRef}
        setOptions={{
          enableBasicAutocompletion: true,
          enableLiveAutocompletion: true,
          enableSnippets: true,
          showLineNumbers: true,
        }}
        style={{
          width: '100%',
          height: '100%',
        }}
        className="h-full"
      />
    </Box>
  );
};

export default CodeEditor;
